import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { updateEntities } from '@redux/entities'

import api from '@functions/api'

import type { ContactUserModel } from '@models/contactUser'
import type { AppDispatch } from '@redux/store'
import type {
  Action, ActionResult, EntityOptions, Errors, FetchRequestPayload, RootReducerState,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/contactUser/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/contactUser/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/contactUser/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/contactUser/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/contactUser/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/contactUser/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/contactUser/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/contactUser/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/contactUser/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/contactUser/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/contactUser/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/contactUser/DELETE_FAILURE'

export type ContactUserModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  updating: boolean,
}

const initialState: ContactUserModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loadedIds: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}): Action {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess(): Action {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []): Action {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest(): Action {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess(): Action {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []): Action {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest(): Action {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess(): Action {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []): Action {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest(): Action {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess(): Action {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []): Action {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadContactUsers(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState): Promise<ActionResult<ContactUserModel[]>> => {
    const loadedForKeys = [...getState().contactUsers.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/contact-users', options)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.CONTACT_USER_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadContactUser(contactUserId: number, options: EntityOptions = {}) {
  return (dispatch: AppDispatch, getState: () => RootReducerState): Promise<ActionResult<ContactUserModel>> => {
    const state = getState()
    const loadedIds = [...state.contactUsers.loadedIds]

    if (contactUserId && !loadedIds.includes(contactUserId)){
      loadedIds.push(contactUserId)
    }

    // Set Loading
    dispatch(fetchRequest({ loadedIds }))

    const promise = api(`/contact-users/${contactUserId}`, options)
      .then(({ data }: { data: ContactUserModel }) => {
        const normalizedJson = normalize(data, Schemas.CONTACT_USER)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createContactUser(contactUser: ContactUserModel, options: EntityOptions = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify(contactUser),
  }

  return (dispatch: AppDispatch): Promise<ActionResult<ContactUserModel>> => {
    dispatch(createRequest())

    const promise = api('/contact-users', options, config)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.CONTACT_USER)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateContactUser(contactUser: ContactUserModel, options: EntityOptions = {}) {
  const config = {
    method: 'PUT',
    data: JSON.stringify(contactUser),
  }

  return (dispatch: AppDispatch): Promise<ActionResult<ContactUserModel>> => {
    dispatch(updateRequest())

    const promise = api(`/contact-users/${contactUser.id}`, options, config)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.CONTACT_USER)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function deleteContactUser(contactUser: ContactUserModel) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch): Promise<ActionResult<ContactUserModel>> => {
    dispatch(deleteRequest())

    const promise = api(`/contact-users/${contactUser.id}`, {}, config)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.CONTACT_USER)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })
    return promise
  }
}

// Reducer
export default function reducer(
  state: ContactUserModuleState = initialState,
  action: Action = { type: '' },
): ContactUserModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
